import React from 'react';
import { Link } from 'react-router-dom';
import { CimmplePayPlan } from '../../data/cimmplePayPricing';
import { productTheme } from '../../utils/productTheme';

interface CimmplePayPricingCardsProps {
  plans: CimmplePayPlan[];
  onSelectTrial: (planName: string) => void;
}

const CimmplePayPricingCards: React.FC<CimmplePayPricingCardsProps> = ({
  plans,
  onSelectTrial,
}) => {
  const theme = productTheme('accent');

  return (
    <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3 max-w-6xl mx-auto">
      {plans.map((plan) => {
        const ctaClassName = `block text-center w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
          plan.highlighted ? theme.ctaGradient : 'btn-secondary'
        }`;

        return (
          <div
            key={plan.id}
            className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 ${
              plan.highlighted
                ? `${theme.cardHighlight} scale-[1.02] xl:scale-105`
                : 'card hover:border-slate-300 dark:hover:border-dark-600'
            }`}
          >
            {plan.badge && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-accent-500 text-white shadow-lg shadow-accent-500/30">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {plan.badge}
              </span>
            )}

            <div className="text-center mb-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">
                {plan.name}
              </h3>
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-5xl font-display font-bold text-slate-900 dark:text-white">
                  {plan.price}
                </span>
                <span className="text-slate-500 dark:text-dark-400">{plan.period}</span>
              </div>
              <p className={`text-sm font-medium mb-3 ${theme.text}`}>{plan.perEmployee}</p>
              <p className="text-slate-500 dark:text-dark-400 text-sm">{plan.description}</p>
            </div>

            <ul className="space-y-3.5 mb-8 flex-1">
              {plan.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-3 text-slate-700 dark:text-dark-300"
                >
                  <svg
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      plan.highlighted ? theme.text : 'text-green-600 dark:text-green-500'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {plan.ctaAction === 'demo' ? (
              <Link to="/contact" className={ctaClassName}>
                {plan.cta}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => onSelectTrial(plan.name)}
                className={ctaClassName}
              >
                {plan.cta}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CimmplePayPricingCards;
